import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MoreVertical, FileText, FileDown, Banknote, CheckCircle2, Pencil, Trash2, ImageIcon, Undo2, Wallet, HardHat } from "lucide-react";
import { money, coladoTotal, concreteDiff, fileUrl, DAYS, DAY_LABEL } from "@/lib/api";

export default function ColadorColumn({ colador, colados, payments, onAddPayment, onLiquidate,
  onEditColado, onDeleteColado, onUnassign, onDownload, onDeleteColador, onEditColador, onDeletePayment }) {

  const totalTrabajo = colados.reduce((s, c) => s + coladoTotal(c), 0);
  const totalPagos = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const pendiente = totalTrabajo - totalPagos;
  const grouped = DAYS.map((d) => ({ day: d, items: colados.filter((c) => c.day === d) })).filter((g) => g.items.length);

  return (
    <div className="bg-white rounded-lg border border-slate-200 flex flex-col w-[340px] shrink-0" data-testid={`colador-col-${colador.id}`}>
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-md bg-slate-900 flex items-center justify-center shrink-0">
              <HardHat className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display font-bold text-slate-900 truncate">{colador.name}</h3>
              {colador.phone && <p className="text-xs text-slate-400">{colador.phone}</p>}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-slate-400 hover:text-slate-700 p-1" data-testid={`colador-menu-${colador.id}`}><MoreVertical className="h-4 w-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem onClick={() => onEditColador(colador)} data-testid={`edit-colador-${colador.id}`}><Pencil className="h-4 w-4 mr-2" />Editar colador</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDownload(colador)} data-testid={`report-colador-${colador.id}`}><FileDown className="h-4 w-4 mr-2" />Reporte individual</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDeleteColador(colador)} className="text-red-600" data-testid={`delete-colador-${colador.id}`}><Trash2 className="h-4 w-4 mr-2" />Eliminar colador</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="mt-3 rounded-md bg-orange-50 border border-orange-100 px-3 py-2 flex items-center justify-between">
          <span className="text-xs font-medium text-orange-700">Pendiente por pagar</span>
          <span className="font-display font-bold text-lg text-orange-700" data-testid={`pending-${colador.id}`}>{money(pendiente)}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scroll p-3 space-y-3 min-h-[80px]">
        {grouped.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Sin colados asignados. Asígnalos desde la Agenda.</p>}
        {grouped.map((g) => (
          <div key={g.day}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{DAY_LABEL[g.day]}</p>
            <div className="space-y-2">
              {g.items.map((c) => (
                <div key={c.id} className="rounded-md border border-slate-200 p-2.5" data-testid={`colado-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <button type="button"
                      onClick={() => c.pdf_file_path ? window.open(fileUrl(c.pdf_file_path), "_blank") : onEditColado(c)}
                      className={`text-sm font-medium text-slate-800 leading-snug text-left flex items-start gap-1 ${c.pdf_file_path ? "hover:text-orange-600 hover:underline cursor-pointer" : ""}`}
                      data-testid={`open-colado-${c.id}`}>
                      {c.pdf_file_path && <FileText className="h-3.5 w-3.5 text-orange-600 mt-0.5 shrink-0" />}
                      <span>{c.obra || "Colado"}</span>
                    </button>
                    <span className="font-display font-bold text-sm text-slate-900 whitespace-nowrap">{money(coladoTotal(c))}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{c.m2 || 0} m² × {money(c.price_per_m2)}{c.extra_amount ? ` + ${money(c.extra_amount)}` : ""}</p>
                  {c.concrete_apply ? (
                    <p className={`text-xs mt-0.5 font-medium ${concreteDiff(c) >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`concrete-line-${c.id}`}>
                      Concreto {c.concrete_m3 ? `${c.concrete_m3} m³ · ` : ""}{concreteDiff(c) >= 0 ? "a favor −" : "se le debe +"}{money(Math.abs(concreteDiff(c)))}
                    </p>
                  ) : null}
                  <div className="flex items-center gap-1.5 mt-2">
                    {c.status === "liquidado" ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1 text-[11px]"><CheckCircle2 className="h-3 w-3" />Liquidado</Badge>
                    ) : (
                      <Button size="sm" onClick={() => onLiquidate(c, colador, coladoTotal(c))} className="h-7 bg-slate-900 hover:bg-slate-800 text-xs px-2.5" data-testid={`liquidate-${c.id}`}>
                        <Banknote className="h-3.5 w-3.5 mr-1" />Liquidar
                      </Button>
                    )}
                    <div className="flex-1" />
                    {c.pdf_file_path && (
                      <a href={fileUrl(c.pdf_file_path)} target="_blank" rel="noreferrer" className="text-orange-600 hover:bg-orange-50 rounded p-1" data-testid={`colado-pdf-${c.id}`}><FileText className="h-4 w-4" /></a>
                    )}
                    <button onClick={() => onEditColado(c)} className="text-slate-400 hover:text-slate-700 p-1" data-testid={`edit-colado-${c.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onUnassign(c)} title="Regresar a agenda" className="text-slate-400 hover:text-slate-700 p-1" data-testid={`unassign-${c.id}`}><Undo2 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onDeleteColado(c)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`delete-colado-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {payments.length > 0 && (
          <div className="pt-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Pagos registrados</p>
            <div className="space-y-1.5">
              {payments.map((p) => (
                <div key={p.id} className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-xs" data-testid={`payment-${p.id}`}>
                  {p.method === "efectivo" ? <Banknote className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> : <ImageIcon className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-800 font-medium leading-snug break-words" data-testid={`payment-note-${p.id}`}>
                      {p.notes || (p.method === "efectivo" ? "Efectivo" : "Transferencia")}
                    </p>
                    <div className="flex items-center gap-1.5 text-slate-400 mt-0.5">
                      <span className="capitalize">{p.method}</span>
                      {p.evidence_file_path && <a href={fileUrl(p.evidence_file_path)} target="_blank" rel="noreferrer" className="text-blue-600 underline" data-testid={`payment-evidence-${p.id}`}>ver comprobante</a>}
                    </div>
                  </div>
                  <span className="font-medium text-slate-800 whitespace-nowrap">{money(p.amount)}</span>
                  <button onClick={() => onDeletePayment(p)} className="text-slate-300 hover:text-red-500 shrink-0 mt-0.5" data-testid={`delete-payment-${p.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-slate-200 space-y-2">
        <div className="flex justify-between text-xs text-slate-500"><span>Trabajo</span><span>{money(totalTrabajo)}</span></div>
        <div className="flex justify-between text-xs text-slate-500"><span>Pagado</span><span>- {money(totalPagos)}</span></div>
        <Button onClick={() => onAddPayment(colador)} variant="outline" className="w-full h-8 text-xs border-slate-300" data-testid={`add-payment-${colador.id}`}>
          <Wallet className="h-3.5 w-3.5 mr-1.5" />Registrar pago / adelanto
        </Button>
      </div>
    </div>
  );
}
